import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Skeleton,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router';

import { finleyAssert } from '@finley-technologies/common';

import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { ErrorBoundary } from '~/contexts/MonitoringProvider';
import { FacilityDetailsRouteParamsStruct } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/helper';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import {
  useGetCreditAgreementDocumentsMetadataQuery,
  useGetCreditAgreementFullArtifactLazyQueryGQLType,
} from '~/core/routes/settings/implementations/implementations.gql.generated';
import { CreditAgreementHistoryTable } from '~/core/routes/settings/implementations/layout/CreditAgreementHistory/CreditAgreementHistoryTable';

const CreditAgreementHistoryDrawerBase = () => {
  const flags = useFeatureFlags();
  const disclosure = useDisclosure();
  const params = useParams();
  const { facilityId } = FacilityDetailsRouteParamsStruct.parse(params);

  const {
    data: creditAgreementDocumentsMetadata,
  } = useGetCreditAgreementDocumentsMetadataQuery({
    variables: { creditFacilityId: facilityId },
  });

  const setPreviousCreditAgreement = useCreditAgreementStore(
    (store) => store.setPreviousCreditAgreement,
  );
  const previousCreditAgreement = useCreditAgreementStore(
    (store) => store.previousCreditAgreement,
  );

  const [
    getCreditAgreement,
  ] = useGetCreditAgreementFullArtifactLazyQueryGQLType();

  const fetchAndSetPreviousCreditAgreement = useCallback(
    async (id: string) => {
      const { data, error } = await getCreditAgreement({
        variables: { creditAgreementId: id },
      });
      if (error) throw error;
      finleyAssert(data, `CreditAgreement with id: ${id} cannot be found`);
      const creditAgreement = JSON.parse(
        data.creditAgreementFullArtifact.creditAgreement,
      );
      setPreviousCreditAgreement(creditAgreement);
    },
    [getCreditAgreement, setPreviousCreditAgreement],
  );

  useEffect(() => {
    if (!previousCreditAgreement && creditAgreementDocumentsMetadata) {
      fetchAndSetPreviousCreditAgreement(
        creditAgreementDocumentsMetadata.creditAgreementDocumentsMetadata[0].id,
      );
    }
  }, [
    creditAgreementDocumentsMetadata,
    fetchAndSetPreviousCreditAgreement,
    previousCreditAgreement,
  ]);

  return (
    <>
      <IconButtonV2
        variant="primary-muted"
        icon={<Icon icon="clock-rotate-left" />}
        aria-label="History"
        isDisabled={flags.productsImplementationPortalPermissions !== 'allow'}
        onClick={disclosure.onOpen}
      />

      <Drawer {...disclosure} size="lg" returnFocusOnClose={false}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader textAlign="left" mt="1.5rem" px="3rem" pb="0">
            <Typography variant="section-title">Versions</Typography>
          </DrawerHeader>
          <Divider my="1.5rem" />
          <DrawerBody>
            <Flex direction="column" gap="2rem">
              {!creditAgreementDocumentsMetadata ? (
                <Skeleton />
              ) : (
                <CreditAgreementHistoryTable
                  creditAgreementDocumentsMetadata={
                    creditAgreementDocumentsMetadata
                  }
                  fetchAndSetPreviousCreditAgreement={
                    fetchAndSetPreviousCreditAgreement
                  }
                />
              )}
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export const CreditAgreementHistoryDrawer = () => (
  <ErrorBoundary
    fallback={({ showErrorToast, message }) => {
      showErrorToast(message);
      return (
        <IconButtonV2
          variant="primary-muted"
          icon={<Icon icon="clock-rotate-left" />}
          aria-label="History"
          isDisabled
        />
      );
    }}
  >
    <CreditAgreementHistoryDrawerBase />
  </ErrorBoundary>
);
